/* ============================================================
   ORION PRE LADDER — quantity-break nudge in the cart drawer.

   For one product whose free units come from automatic discounts at
   fixed quantities (PRE: 3 bags = 1 free, 6 bags = 3 free), a line
   under its cart row says how many more unlock the next free unit,
   with a one-tap add. Config comes from the cart settings section
   (#orion-cart-config "ladder"); nothing here is product-specific.

   Plays by the drawer's rules: Horizon morphs the rows on every
   cart:update, so the note is re-injected after each morph and only
   rewritten when its state changes (no flicker). Quantity writes go
   through /cart/change.js with the drawer's section ids and dispatch
   the same cart:update shape orion-cart.js uses, under its own
   sourceId so the ship bar and upsells refresh.
   ============================================================ */
(() => {
  const cfgEl = document.getElementById('orion-cart-config');
  let cfg = {};
  try { cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {}; } catch (e) { return; }
  const ladder = cfg.ladder;
  if (!ladder || !ladder.handle || !Array.isArray(ladder.tiers) || !ladder.tiers.length) return;
  const tiers = ladder.tiers.filter((t) => t.qty > 0).sort((a, b) => a.qty - b.qty);
  const S = ladder.strings || {};
  const fmt = (s, vars) => String(s || '').replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));

  let cart = null;
  let busy = false;
  let frame = 0;
  const drawer = () => document.querySelector('cart-drawer-component');
  const sectionIds = () => [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

  async function fetchCart() {
    try { const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } }); cart = await r.json(); }
    catch (e) { cart = null; }
    return cart;
  }

  /* Free units at a quantity: the largest tier reached (discounts do not stack). */
  const freeAt = (q) => tiers.filter((x) => x.qty <= q).reduce((m, x) => Math.max(m, x.free), 0);
  const paidAt = (q) => q - freeAt(q);
  const money = (cents) => '$' + (cents / 100).toFixed(2);

  /* The line states the OFFER and what the next step really costs. "Add 2
     more" read as paying for two; from one bag the free one costs one more
     bag, and from two it costs nothing. */
  function hint(qty, unit) {
    const next = tiers.find((x) => x.qty > qty);
    if (!next) {
      const reached = tiers.filter((x) => x.qty <= qty).pop();
      return reached ? { text: fmt(S.top, { free: reached.free }), add: 0 } : null;
    }
    const cost = (paidAt(next.qty) - paidAt(qty)) * unit;
    return {
      text: fmt(S.offer, { paid: next.qty - next.free, free: next.free }),
      add: next.qty - qty,
      button: cost > 0 ? fmt(S.button, { price: money(cost), n: next.qty - qty }) : fmt(S.freeButton, { free: next.free }),
    };
  }

  /* The subscription slot renders after the LAST line. A ladder product sells
     no plan, so when its line is last the "Make it a subscription" card reads
     as if it belonged to it: detach the card visually in that case. The
     generic subscribe banner (nothing-convertible carts) stands down entirely
     while a ladder line is present, because the line right above it cannot
     subscribe and the pitch reads as its label. */
  function placeSubSlot(d) {
    const rows = [...d.querySelectorAll('.cart-items__table-row')];
    const last = rows[rows.length - 1];
    const lastItem = last && last.dataset.key && cart.items.find((i) => i.key === last.dataset.key);
    const ladderLast = !!(lastItem && lastItem.handle === ladder.handle);
    const hasLadderLine = cart.items.some((i) => i.handle === ladder.handle);
    d.querySelectorAll('.orion-suboffer').forEach((el) => el.classList.toggle('orion-suboffer--detached', ladderLast));
    d.querySelectorAll('.orion-subbanner').forEach((el) => { el.style.display = hasLadderLine ? 'none' : ''; });
  }

  function render() {
    const d = drawer();
    if (!d || !cart) return;
    placeSubSlot(d);
    d.querySelectorAll('.cart-items__table-row').forEach((row) => {
      const key = row.dataset.key;
      const item = key && cart.items.find((i) => i.key === key);
      const old = row.querySelectorAll('.orion-ladder');
      if (!item || item.handle !== ladder.handle || item.selling_plan_allocation) { old.forEach((n) => n.remove()); return; }
      const h = hint(item.quantity, item.original_price || item.price);
      if (!h) { old.forEach((n) => n.remove()); return; }
      const state = key + '|' + item.quantity + '|' + (item.original_price || item.price);
      let el = old[0];
      for (let i = 1; i < old.length; i += 1) old[i].remove(); // the morph can clone it
      if (el && el.dataset.state === state) return;
      if (!el) {
        el = document.createElement('div');
        el.className = 'orion-ladder';
        (row.querySelector('.cart-items__details') || row).appendChild(el);
      }
      el.dataset.state = state;
      el.classList.toggle('orion-ladder--top', h.add === 0);
      el.replaceChildren();
      const text = document.createElement('span');
      text.className = 'orion-ladder__text';
      text.textContent = h.text;
      el.appendChild(text);
      if (h.add > 0) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'orion-ladder__add';
        btn.textContent = h.button;
        btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); bump(item.key, item.quantity + h.add, btn); });
        el.appendChild(btn);
      }
    });
  }

  async function bump(key, quantity, btn) {
    if (busy) return;
    busy = true;
    btn.disabled = true;
    try {
      const payload = { id: key, quantity };
      const ids = sectionIds();
      if (ids.length) payload.sections = ids.join(',');
      const r = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error('change failed');
      const resp = await r.json();
      await fetchCart();
      /* same event shape as orion-cart.js, own sourceId so its dynamic parts refresh */
      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = { resource: cart, sourceId: 'orion-pre-ladder', data: { source: 'product-form-component', itemCount: cart ? cart.item_count : undefined, sections: resp.sections || undefined } };
      document.dispatchEvent(ev);
    } catch (e) {
      btn.disabled = false;
    } finally {
      busy = false;
      schedule();
    }
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => { frame = 0; render(); });
  }

  async function refresh() { await fetchCart(); schedule(); }

  function init() {
    refresh();
    document.addEventListener('cart:update', refresh);
    document.addEventListener('orion:cart:added', refresh);
    const d = drawer();
    if (d) {
      new MutationObserver(schedule).observe(d, { childList: true, subtree: true });
      d.addEventListener('dialog:open', refresh);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
